import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { generateQuestion, generateBalloons, checkEquation } from '../utils/questionGenerator';
import { getJungleBeat, destroyJungleBeat } from '../utils/jungleBeat';
import { playCorrectSound, playWrongSound, playPopSound } from '../utils/soundEffects';
import { EquationBuilder, GameTimer } from '../components/game/GameComponents';
import { X, Volume2, VolumeX } from 'lucide-react';
import confetti from 'canvas-confetti';

const STAR_URL =
  'https://static.prod-images.emergentagent.com/jobs/0e7ec82b-be21-4167-ae09-299c58207254/images/51c2f4d391b08733a1282c5c803c88045bd7e3e5993310b66fbe2ae387825c7c.png';

export default function GamePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const mode = searchParams.get('mode') || 'practice';
  const operation = searchParams.get('operation') || 'addition';

  const totalQuestions = mode === 'test' ? 20 : 10;
  const hasTimer = mode === 'test';
  const totalTime = 300; // 5 minutes

  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [score, setScore] = useState(0);
  const [balloons, setBalloons] = useState([]);
  const [slots, setSlots] = useState([null, null, null]);
  const [timeLeft, setTimeLeft] = useState(totalTime);
  const [feedback, setFeedback] = useState(null);
  const [usedBalloonIds, setUsedBalloonIds] = useState(new Set());
  const [gameStartTime] = useState(Date.now());
  const [isFinished, setIsFinished] = useState(false);
  const [musicOn, setMusicOn] = useState(true);

  const scoreRef = useRef(0);
  const currentIndexRef = useRef(0);
  const timerRef = useRef(null);
  const beatStarted = useRef(false);

  // Keep refs in sync
  useEffect(() => {
    scoreRef.current = score;
  }, [score]);
  useEffect(() => {
    currentIndexRef.current = currentIndex;
  }, [currentIndex]);

  // Start jungle beat music on first interaction
  useEffect(() => {
    const startBeat = () => {
      if (!beatStarted.current && musicOn) {
        const beat = getJungleBeat();
        beat.start();
        beatStarted.current = true;
      }
      document.removeEventListener('click', startBeat);
    };
    document.addEventListener('click', startBeat);
    return () => document.removeEventListener('click', startBeat);
  }, [musicOn]);

  // Toggle music
  useEffect(() => {
    if (beatStarted.current) {
      const beat = getJungleBeat();
      if (musicOn && !beat.isPlaying) {
        beat.start();
      } else if (!musicOn && beat.isPlaying) {
        beat.stop();
      }
    }
  }, [musicOn]);

  // Cleanup music on unmount
  useEffect(() => {
    return () => {
      destroyJungleBeat();
    };
  }, []); // mount-only: cleanup on unmount

  // Generate questions on mount
  useEffect(() => {
    const qs = [];
    for (let i = 0; i < totalQuestions; i++) {
      qs.push(generateQuestion(operation));
    }
    setQuestions(qs);
    if (qs.length > 0) {
      setBalloons(generateBalloons(qs[0], operation));
    }
  }, [operation, totalQuestions]);

  // Timer
  useEffect(() => {
    if (!hasTimer) return;
    timerRef.current = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [hasTimer]);

  const finishGame = useCallback(() => {
    if (isFinished) return;
    setIsFinished(true);
    clearInterval(timerRef.current);
    const timeTaken = Math.floor((Date.now() - gameStartTime) / 1000);
    navigate(
      `/results?mode=${mode}&operation=${operation}&score=${scoreRef.current}&total=${totalQuestions}&time=${timeTaken}`
    );
  }, [isFinished, gameStartTime, mode, operation, totalQuestions, navigate]);

  // Time up
  useEffect(() => {
    if (hasTimer && timeLeft === 0) {
      finishGame();
    }
  }, [timeLeft, hasTimer, finishGame]);

  const currentQuestion = questions[currentIndex];

  const handleBalloonClick = (balloon) => {
    if (feedback) return;
    if (usedBalloonIds.has(balloon.id)) return;

    const newSlots = [...slots];
    const newUsed = new Set(usedBalloonIds);

    if (balloon.type === 'number') {
      if (newSlots[0] === null) {
        newSlots[0] = balloon;
        newUsed.add(balloon.id);
      } else if (newSlots[2] === null) {
        newSlots[2] = balloon;
        newUsed.add(balloon.id);
      } else {
        // Both number slots full - replace first
        return;
      }
    } else if (balloon.type === 'operation') {
      if (newSlots[1] !== null) {
        newUsed.delete(newSlots[1].id);
      }
      newSlots[1] = balloon;
      newUsed.add(balloon.id);
    }

    setSlots(newSlots);
    setUsedBalloonIds(newUsed);
    if (musicOn) playPopSound();
  };

  const handleSlotClick = (index) => {
    if (feedback) return;
    if (slots[index] === null) return;

    const newUsed = new Set(usedBalloonIds);
    newUsed.delete(slots[index].id);

    const newSlots = [...slots];
    newSlots[index] = null;

    setSlots(newSlots);
    setUsedBalloonIds(newUsed);
    if (musicOn) playPopSound();
  };

  const handleCheck = () => {
    if (!slots[0] || !slots[1] || !slots[2] || !currentQuestion) return;

    const isCorrect = checkEquation(slots[0], slots[1], slots[2], currentQuestion.answer);

    if (isCorrect) {
      setFeedback('correct');
      setScore((prev) => prev + 1);
      if (musicOn) playCorrectSound();
      confetti({ particleCount: 60, spread: 70, origin: { y: 0.7 } });

      setTimeout(() => {
        nextQuestion();
      }, 1200);
    } else {
      setFeedback('wrong');
      if (musicOn) playWrongSound();
      setTimeout(() => {
        setFeedback(null);
        clearSlots();
      }, 1000);
    }
  };

  const clearSlots = () => {
    setSlots([null, null, null]);
    setUsedBalloonIds(new Set());
  };

  const nextQuestion = () => {
    const nextIdx = currentIndex + 1;
    if (nextIdx >= totalQuestions) {
      // Need to account for the correct answer in score
      setTimeout(() => finishGame(), 100);
      return;
    }
    setCurrentIndex(nextIdx);
    setBalloons(generateBalloons(questions[nextIdx], operation));
    setSlots([null, null, null]);
    setUsedBalloonIds(new Set());
    setFeedback(null);
  };

  if (!currentQuestion) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="glass-panel p-8 text-center">
          <p className="font-heading text-2xl font-bold text-amber-950">Loading...</p>
        </div>
      </div>
    );
  }

  const operationLabel =
    operation === 'mixed'
      ? 'Mixed'
      : operation.charAt(0).toUpperCase() + operation.slice(1);

  return (
    <div className="min-h-screen flex flex-col p-3 sm:p-4 max-w-2xl mx-auto">
      {/* Top Bar */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <img src={STAR_URL} alt="star" className="w-12 h-12" />
          <span
            data-testid="score-display"
            className={`font-heading text-4xl sm:text-5xl font-black text-amber-950 ${
              feedback === 'correct' ? 'animate-score-bump' : ''
            }`}
          >
            {score}/{totalQuestions}
          </span>
        </div>

        <div className="flex items-center gap-3">
          <span className="font-body text-sm font-bold text-amber-800 bg-white/70 px-3 py-1 rounded-full">
            {operationLabel}
          </span>
          <span
            data-testid="question-counter"
            className="font-body text-sm font-bold text-amber-900 bg-white/70 px-3 py-1 rounded-full"
          >
            {currentIndex + 1}/{totalQuestions}
          </span>
        </div>

        <div className="flex items-center gap-2">
          <button
            data-testid="toggle-music"
            onClick={() => setMusicOn((v) => !v)}
            className="bg-white/70 p-2 rounded-full hover:bg-white/90 transition-colors"
            title={musicOn ? 'Mute music' : 'Play music'}
          >
            {musicOn ? (
              <Volume2 className="w-5 h-5 text-amber-800" />
            ) : (
              <VolumeX className="w-5 h-5 text-amber-800" />
            )}
          </button>
          <button
            data-testid="quit-game"
            onClick={() => navigate('/jungle-math')}
            className="bg-white/70 p-2 rounded-full hover:bg-white/90 transition-colors"
          >
            <X className="w-5 h-5 text-amber-800" />
          </button>
        </div>
      </div>

      {/* Timer */}
      {hasTimer && <GameTimer timeLeft={timeLeft} totalTime={totalTime} />}

      {/* Target Number */}
      <div className="text-center mb-4">
        <div className="inline-block glass-panel px-8 py-4">
          <p className="font-body text-sm text-amber-800 font-bold uppercase tracking-wider">
            Make this number!
          </p>
          <p
            data-testid="target-number"
            className="font-heading text-5xl sm:text-6xl font-black text-amber-950 leading-none mt-1"
          >
            {currentQuestion.answer}
          </p>
        </div>
      </div>

      {/* Balloons Area */}
      <div className="flex-1 flex items-center justify-center mb-4">
        <div className="flex flex-wrap justify-center gap-3 sm:gap-4 max-w-lg">
          {balloons.map((balloon, i) => {
            const isUsed = usedBalloonIds.has(balloon.id);
            const isOp = balloon.type === 'operation';
            return (
              <button
                key={balloon.id}
                data-testid={`balloon-${balloon.id}`}
                onClick={() => handleBalloonClick(balloon)}
                disabled={isUsed}
                className={`
                  relative balloon-string balloon-gloss
                  ${isOp ? 'w-14 h-14 sm:w-16 sm:h-16' : 'w-16 h-16 sm:w-20 sm:h-20'}
                  rounded-full flex items-center justify-center
                  font-heading ${isOp ? 'text-2xl sm:text-3xl' : 'text-xl sm:text-2xl'} font-black text-white
                  shadow-lg transition-all duration-300
                  ${isUsed ? 'opacity-20 scale-50 pointer-events-none' : 'hover:scale-110 hover:shadow-xl cursor-pointer active:scale-95'}
                  ${balloon.color}
                  ${!isUsed ? 'animate-float-in balloon-bob' : ''}
                `}
                style={{ animationDelay: `${i * 0.08}s` }}
              >
                {balloon.display}
              </button>
            );
          })}
        </div>
      </div>

      {/* Equation Builder */}
      <EquationBuilder
        slots={slots}
        currentAnswer={currentQuestion.answer}
        feedback={feedback}
        onSlotClick={handleSlotClick}
        onClear={clearSlots}
        onCheck={handleCheck}
      />
    </div>
  );
}
